/**
 * P5-01: TopBar —— 顶部静态区 React 化。
 *
 * 对齐旧 index.html:398-454 结构：
 * - 标题/作者/αβML 链接
 * - #motd 占位（兼容外部脚本，保持空）
 * - #devPanel（FPS + cheats）
 * - #headerLinks：Toolbar 面板 + autosave/save toast
 * - .links-block：Save/Options/Reset/DEV/Wipe/Get the app/Steam/Version/NewVersion
 *
 * 数据与交互：
 * - 版本号读取全局 window.version / window.buildRevision；
 * - DEV 按钮与 cheat 面板受 game.isLocalhost / game.devMode 驱动；
 * - autosave/save toast 由 bridge/toasts 触发；
 * - 新版本提示由 bridge/GameBridge 的 newVersion listener 触发；
 * - toggleCenter 不在本组件内（见 P5-03 FooterBar）。
 */
import { useCallback, useEffect, useState } from "react";
import type { GamePage } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import {
  getGame,
  setNewVersionListener,
} from "../../bridge/GameBridge";
import { setToastListener, type ToastType } from "../../bridge/toasts";
import ToolbarPanel from "../toolbar/Toolbar";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function useVersion(): { displayed: string; revision: number } {
  const globals = window as {
    version?: string;
    buildRevision?: number;
  };
  const version = globals.version ?? "1502";
  const revision = globals.buildRevision ?? 0;
  const displayed = version.split("").join(".");
  return { displayed, revision };
}

function useDevMode(): { isLocalhost: boolean; devMode: boolean } {
  return useGameTick(
    useCallback(
      (game: GamePage | null) => ({
        isLocalhost: !!game?.isLocalhost,
        devMode: !!game?.devMode,
      }),
      [],
    ),
    (a, b) => a.isLocalhost === b.isLocalhost && a.devMode === b.devMode,
  );
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

function useToasts(): {
  autosave: boolean;
  save: boolean;
} {
  const [toasts, setToasts] = useState<{ autosave: boolean; save: boolean }>({
    autosave: false,
    save: false,
  });

  useEffect(() => {
    const timers: number[] = [];
    setToastListener((type: ToastType) => {
      setToasts((prev) => ({ ...prev, [type]: true }));
      timers.push(
        window.setTimeout(() => {
          setToasts((prev) => ({ ...prev, [type]: false }));
        }, 1200),
      );
    });
    return () => {
      setToastListener(null);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  return toasts;
}

// ---------------------------------------------------------------------------
// New-version hint
// ---------------------------------------------------------------------------

function useNewVersion(): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setNewVersionListener((available: boolean) => setVisible(available));
    return () => setNewVersionListener(null);
  }, []);

  return visible;
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function TitleBlock() {
  return (
    <div
      style={{
        paddingLeft: "10px",
        minWidth: "0px",
        maxWidth: "400px",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <span className="gameTitle">Kittens Game </span>
      <span style={{ fontSize: "small" }}>
        by{" "}
        <a
          href="http://kittensgame.com/"
          className="copyright"
          target="_blank"
          tabIndex={-1} rel="noreferrer"
        >
          bloodrizer
        </a>
      </span>
      <a href="#" style={{ color: "red" }} />
      <span id="motd" />
      <span style={{ fontSize: "small" }}>
        <a
          href="http://kittensgame.com/alpha"
          tabIndex={0}
          title="experimental, unstable"
          target="_blank"
          style={{ opacity: 0.6 }} rel="noreferrer"
        >
          α
        </a>
        <a
          href="http://kittensgame.com/beta"
          tabIndex={0}
          title="experimental, stable"
          target="_blank" rel="noreferrer"
        >
          β
        </a>
        <a
          href="http://kittensgame.com/web"
          tabIndex={0}
          title="RC, nighty build"
          target="_blank" rel="noreferrer"
        >
          ML
        </a>
      </span>
    </div>
  );
}

function DevPanel() {
  const { isLocalhost, devMode } = useDevMode();

  const handleRestartFPS = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    getGame()?.restartFPSCounters?.();
  }, []);

  if (!isLocalhost) {
    return null;
  }

  return (
    <div
      id="devPanel"
      style={{ display: "inline-block", paddingLeft: "15px", fontSize: "14px" }}
    >
      <div id="devPanelFPS" onClick={handleRestartFPS} />
      <div
        id="devPanelCheats"
        style={{ display: devMode ? "block" : "none" }}
      >
        <a href="#" onClick={(e) => { e.preventDefault(); getGame()?.science?.unlockAll(); }}>All techs</a>{" "}
        <a href="#" onClick={(e) => { e.preventDefault(); getGame()?.workshop?.unlockAll(); }}>All upgrades</a>{" "}
        <a href="#" onClick={(e) => { e.preventDefault(); getGame()?.resPool?.maxAll(); }}>All resources</a>{" "}
        <a href="#" onClick={(e) => { e.preventDefault(); getGame()?.bld?.devAddStorage(); }}>More storage</a>{" "}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            const g = getGame();
            if (g) {
              g.unlockedSchemes = g.ui.allSchemes;
              g.ui.updateOptions();
            }
          }}
        >
          All themes
        </a>
      </div>
    </div>
  );
}

function HeaderToolbar() {
  // Toolbar 与 TopBar 在同一个 React root 内提交。React 18 的 root.render
  // 不保证同步创建 DOM，不能让 index.html 紧接着查找锚点再挂第二个 root。
  return (
    <div id="headerToolbar" style={{ fontSize: "12px" }}>
      <ToolbarPanel />
    </div>
  );
}

function HeaderLinks() {
  const toasts = useToasts();

  return (
    <div id="headerLinks">
      <HeaderToolbar />
      <span
        id="autosaveTooltip"
        style={{
          paddingRight: "15px",
          fontSize: "12px",
          opacity: toasts.autosave ? 1 : 0,
          transition: "opacity 1200ms ease-out",
        }}
      >
        {t("ui.autosave.tooltip")}
      </span>
      <span
        id="saveTooltip"
        style={{
          paddingRight: "15px",
          fontSize: "12px",
          opacity: toasts.save ? 1 : 0,
          transition: "opacity 1200ms ease-out",
        }}
      >
        {t("ui.save.tooltip")}
      </span>
    </div>
  );
}

function BottomLinks() {
  const { displayed, revision } = useVersion();
  const newVersionVisible = useNewVersion();
  const { isLocalhost } = useDevMode();

  const handleSave = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      getGame()?.saveUI?.();
    },
    [],
  );

  const handleReset = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      getGame()?.reset?.();
    },
    [],
  );

  const handleWipe = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      // wipe() 在 index.html 中定义为全局函数；保持兼容。
      (window as { wipe?: () => void }).wipe?.();
    },
    [],
  );

  const handleReload = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      getGame()?.reload?.();
    },
    [],
  );

  return (
    <div className="links-block" style={{ paddingTop: "3px", display: "inline-flex" }}>
      <a id="save-link" href="#" onClick={handleSave}>
        {t("menu.save")}
      </a>{" "}
      |{" "}
      <a
        id="options-link"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          window.KGNewUI?.dialogs?.showOptions?.();
        }}
      >
        {t("menu.options")}
      </a>{" "}
      |{" "}
      <a id="reset-link" href="#" onClick={handleReset}>
        {t("menu.reset")}
      </a>{" "}
      |{" "}
      {isLocalhost && (
        <span id="devModeButton" style={{ display: "inline" }}>
          <a
            href="#"
            id="devBtn"
            onClick={(e) => {
              e.preventDefault();
              // dev() 在 index.html 中定义为全局函数；设置 game.devMode 并触发 render。
              (window as { dev?: () => void }).dev?.();
            }}
          >
            DEV
          </a>{" "}
          |{" "}
        </span>
      )}
      <a id="wipe-link" href="#" onClick={handleWipe}>
        {t("menu.wipe")}
      </a>{" "}
      |{" "}
      <a
        id="getTheApp-link"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          window.KGNewUI?.dialogs?.showApp?.();
        }}
        style={{ fontWeight: "bold", color: "#666" }}
      >
        {t("menu.getTheApp")}
      </a>{" "}
      |{" "}
      <a
        id="getTheApp-link"
        href="https://store.steampowered.com/app/1097410/Kittens_Game/"
        target="_blank"
        style={{ fontWeight: "bold", color: "#666" }} rel="noreferrer"
      >
        Steam
      </a>{" "}
      |{" "}
      <a href="changelog.txt" target="_blank">
        Ver <span id="versionLink">{displayed}.r{revision}</span>
      </a>
      {newVersionVisible && (
        <a
          href="#"
          id="newVersion"
          onClick={handleReload}
          title="New version available!"
        >
          ↻
        </a>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exported panel
// ---------------------------------------------------------------------------

export default function TopBar() {
  return (
    <div id="topBar" aria-label="Header panel">
      <TitleBlock />
      <DevPanel />
      <HeaderLinks />
      <BottomLinks />
    </div>
  );
}

// Re-export helpers for tests
export { useVersion, useDevMode, useToasts, useNewVersion };
